import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useParams, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { lotService, notificationService } from "../utils/api";
import { useLot } from "../contexts/LotContext";
import { useDirtyState } from "../contexts/DirtyStateContext";
import { useMultiLot } from "../contexts/MultiLotContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "../contexts/ThemeContext";
import Modal from "./Modal";
import Menu, { MenuItem } from "./Menu";
import "./Sidebar.css";
import AccountModal from "./AccountModal";
import {
  isSuperadminModeEnabled,
  setSuperadminModeEnabled,
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
} from "./SuperadminHeader";
import { useWalkthrough } from "./Walkthrough/WalkthroughContext";

type SidebarProps = {
  isSidebarOpen?: boolean;
  setIsSidebarOpen?: (open: boolean) => void;
};

type CategoryKey = "overview" | "management" | "administration" | "superadmin";
type UserRole = "" | "owner" | "operator" | "superadmin" | "staff";

type NavItemConfig = {
  path: string;                   // URL segment, e.g. "settings"
  label: string;
  icon: string;
  visibleTo?: UserRole[];         // undefined = visible to all roles
  requiresBetaAi?: boolean;       // also requires visibleTo to pass
  requiresAddons?: boolean;       // also requires ADDONS_ENABLED
  disabledOnMobile?: boolean;
  restrictedInMultiLot?: boolean; // shows warning modal in multi-lot mode
  superadminPath?: boolean;       // uses /lot/:id/superadmin/:path prefix
};

type NavSectionConfig = {
  key: CategoryKey;
  label: string;
  collapsible: boolean;
  superadminOnly?: boolean;       // entire section hidden unless isSuperadmin
  items: NavItemConfig[];
};

const ADDONS_ENABLED = false; // temporarily disabled until implemented
const DEFAULT_OPEN_CATEGORIES: Record<CategoryKey, boolean> = {
  overview: true,
  management: true,
  administration: true,
  superadmin: true,
};

// Single source of truth for nav items: visibility, role access, and multi-lot restrictions.
// To add a new nav item, add an entry here — no other changes needed.
const NAV_SECTIONS: NavSectionConfig[] = [
  {
    key: "overview",
    label: "Overview",
    collapsible: false,
    items: [
      { path: "dashboard",    label: "Dashboard",   icon: "Dashboard" },
      { path: "parallel-ai", label: "Parallel AI",  icon: "AI",
        visibleTo: ["owner", "operator", "superadmin"],
        requiresBetaAi: true, restrictedInMultiLot: true },
    ],
  },
  {
    key: "management",
    label: "Management",
    collapsible: true,
    items: [
      { path: "occupants",     label: "Occupants",     icon: "Occupants" },
      { path: "registry",      label: "Registry",      icon: "Registry" },
      { path: "violators",     label: "Violators",     icon: "Violators" },
      { path: "notifications", label: "Notifications", icon: "Notifications" },
    ],
  },
  {
    key: "administration",
    label: "Administration",
    collapsible: true,
    items: [
      { path: "settings", label: "Settings", icon: "Settings",
        visibleTo: ["owner", "operator", "superadmin"], restrictedInMultiLot: true },
      { path: "advanced", label: "Advanced", icon: "Advanced",
        visibleTo: ["owner", "operator", "superadmin"],
        disabledOnMobile: true, restrictedInMultiLot: true },
      { path: "addons",   label: "Addons",   icon: "Addons",
        visibleTo: ["owner", "superadmin"],
        requiresAddons: true, disabledOnMobile: true },
      { path: "users",    label: "Users",    icon: "Users",
        restrictedInMultiLot: true },
    ],
  },
  {
    key: "superadmin",
    label: "Superadmin",
    collapsible: true,
    superadminOnly: true,
    items: [
      { path: "cameras",  label: "Cameras",  icon: "Cameras",  superadminPath: true, restrictedInMultiLot: true },
      { path: "alpr-log", label: "ALPR Log", icon: "ALPRLog",  superadminPath: true, restrictedInMultiLot: true },
      { path: "sessions", label: "Sessions", icon: "Sessions", superadminPath: true, restrictedInMultiLot: true },
      { path: "disputes", label: "Disputes", icon: "Disputes", superadminPath: true, restrictedInMultiLot: true },
    ],
  },
];

// Returns the items in a section that the current user can see.
const getVisibleItems = (
  section: NavSectionConfig,
  role: UserRole,
  isSuperadmin: boolean,
  betaAiEnabled: boolean,
): NavItemConfig[] => {
  if (section.superadminOnly && !isSuperadmin) return [];
  return section.items.filter((item) => {
    if (item.visibleTo && !item.visibleTo.includes(role)) return false;
    if (item.requiresBetaAi && !betaAiEnabled) return false;
    if (item.requiresAddons && !ADDONS_ENABLED) return false;
    return true;
  });
};

const Sidebar = ({
  isSidebarOpen: sidebarOpenProp,
  setIsSidebarOpen: setSidebarOpenProp,
}: SidebarProps) => {
  const { lotId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userLots, fetchUserLots, logout } = useUser();
  const [lotName, setLotName] = useState<string>("");
  const [showChangeLot, setShowChangeLot] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<UserRole>("");
  const [operators, setOperators] = useState<any[]>([]);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isSuperadmin, setIsSuperadmin] = useState<boolean>(() => isSuperadminModeEnabled());
  const [isBetaAiEnabled, setIsBetaAiEnabled] = useState<boolean>(false);
  const { isDirty, setIsDirty } = useDirtyState();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [internalSidebarOpen, setInternalSidebarOpen] = useState<boolean>(false);
  const isSidebarOpen =
    typeof sidebarOpenProp === "boolean" ? sidebarOpenProp : internalSidebarOpen;
  const setIsSidebarOpen = setSidebarOpenProp || setInternalSidebarOpen;
  const { themeMode, cycleThemeMode } = useTheme();
  const lotContext = useLot();
  const queryClient = useQueryClient();
  const { active: walkthroughActive } = useWalkthrough();

  // Multi-lot selection state
  const {
    selectedLots,
    primaryLotId,
    isMultiLotMode,
    isMobile: isMultiLotMobileDisabled,
    toggleLotSelection,
    clearToSingleLot,
    getDisplayLotName,
    getPrimaryLot,
  } = useMultiLot();
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  const [showRestrictedPageModal, setShowRestrictedPageModal] = useState(false);
  const [restrictedPageTarget, setRestrictedPageTarget] = useState<string | null>(null);
  const [openCategories, setOpenCategories] = useState<Record<CategoryKey, boolean>>(
    DEFAULT_OPEN_CATEGORIES
  );
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isLotMenuOpen, setIsLotMenuOpen] = useState(false);
  const [isSidebarReady, setIsSidebarReady] = useState(false);
  const [shouldFadeIn, setShouldFadeIn] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const previousLotIdRef = useRef<string | null>(null);
  const previousSuperadminRef = useRef<boolean>(isSuperadmin);
  const fadeTimeoutRef = useRef<number | null>(null);
  const animTimeoutRef = useRef<number | null>(null);
  const animImgRef = useRef<HTMLImageElement | null>(null);
  const accountRole = (user?.role || "").toLowerCase();
  const isAccountSuperadmin = accountRole === "superadmin";

  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      if (animTimeoutRef.current) {
        window.clearTimeout(animTimeoutRef.current);
        animTimeoutRef.current = null;
      }
    };
  }, []);

  // Track shift key for multi-lot selection (desktop only)
  useEffect(() => {
    if (isMobile || isMultiLotMobileDisabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") setIsShiftHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") setIsShiftHeld(false);
    };
    const handleBlur = () => setIsShiftHeld(false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [isMobile, isMultiLotMobileDisabled]);

  useEffect(() => {
    if (!lotId) {
      setOperators([]);
      setUserRole("");
      setIsBetaAiEnabled(false);
      return;
    }

    setOperators([]);
    setIsBetaAiEnabled(false);

    if (!isSuperadmin) {
      setUserRole("");
    }
  }, [lotId, isSuperadmin]);

  const lastDashboardTab = localStorage.getItem("lastDashboardTab") || "revenue";

  const { data: unreadNotificationCount } = useQuery({
    queryKey: ["notificationCount", lotId],
    queryFn: () => notificationService.countUnreadNotificationsByLot(lotId || ""),
    enabled: !!lotId,
    staleTime: 300,
    retry: 1,
  });

  useEffect(() => {
    const isOnNotificationsPage = location.pathname === `/lot/${lotId}/notifications`;

    if (!isOnNotificationsPage) {
      const wasOnNotificationsPage = sessionStorage.getItem("wasOnNotificationsPage") === "true";
      if (wasOnNotificationsPage) {
        queryClient.invalidateQueries({ queryKey: ["notificationCount", lotId] });
        sessionStorage.removeItem("wasOnNotificationsPage");
      }
    }

    if (isOnNotificationsPage) {
      sessionStorage.setItem("wasOnNotificationsPage", "true");
    }
  }, [location.pathname, lotId, queryClient]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("wasOnNotificationsPage");
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isSuperadminFromUrl = urlParams.get("superadmin") === "true";
    const superadminToken = sessionStorage.getItem("superadmin_token_transfer");

    if (isSuperadminFromUrl || superadminToken) {
      setSuperadminModeEnabled(true);
      setUserRole("superadmin");
    }
  }, [location.search]);

  useEffect(() => {
    if (!isAccountSuperadmin) {
      if (isSuperadminModeEnabled()) {
        setSuperadminModeEnabled(false);
      }
      setIsSuperadmin(false);
      return;
    }

    setIsSuperadmin(isSuperadminModeEnabled());
  }, [isAccountSuperadmin]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setIsSuperadmin(detail.enabled);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setIsSuperadmin(event.newValue === "true");
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadLotData = async () => {
      if (!lotId || !user?.userId) {
        previousLotIdRef.current = null;
        setIsSidebarReady(false);
        return;
      }

      const lotChanged = previousLotIdRef.current !== lotId;
      const superadminChanged = previousSuperadminRef.current !== isSuperadmin;
      if (!lotChanged && !superadminChanged && isSidebarReady) {
        return;
      }

      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      setShouldFadeIn(false);
      setIsSidebarReady(false);

      try {
        let resolvedLotData: any =
          lotContext && lotContext.lotData && lotContext.lotData.lotId === lotId
            ? lotContext.lotData
            : null;

        if (!resolvedLotData) {
          try {
            const lotData = await lotService.getLotById(lotId);
            resolvedLotData = lotData && lotData.data ? lotData.data : lotData;
          } catch (lotError) {
            console.error("Failed to fetch lot details", lotError);
          }
        }

        if (resolvedLotData) {
          const lotLabel = resolvedLotData.lotName || "";
          setLotName(lotLabel);
          localStorage.setItem(`lot_${lotId}_name`, lotLabel);
          setIsBetaAiEnabled(!!resolvedLotData.allowBetaAi);
        } else {
          setLotName("");
          setIsBetaAiEnabled(false);
        }

        let resolvedRole: UserRole = "staff";
        let operatorsList: any[] = [];

        if (isSuperadmin) {
          resolvedRole = "superadmin";
          if (!isCancelled) {
            setShowChangeLot(false);
            localStorage.setItem("user_has_multiple_lots", "false");
          }
        } else if (resolvedLotData?.ownerId === user.userId) {
          resolvedRole = "owner";
        } else {
          try {
            operatorsList = await lotService.getOperators(lotId);
          } catch (operatorError) {
            console.error("Failed to fetch operators for lot", operatorError);
          }

          if (operatorsList.length > 0) {
            const isOperator = operatorsList.some((op: any) => op.userId === user.userId);
            resolvedRole = isOperator ? "operator" : "staff";
          } else {
            resolvedRole = "staff";
          }
        }

        if (!isCancelled) {
          setOperators(operatorsList);
          setUserRole(resolvedRole);
        }
      } catch (error) {
        console.error("Error loading lot data in Sidebar:", error);
      } finally {
        if (!isCancelled) {
          if (previousLotIdRef.current !== lotId) {
            setShouldFadeIn(true);
            fadeTimeoutRef.current = window.setTimeout(() => {
              setShouldFadeIn(false);
              fadeTimeoutRef.current = null;
            }, 300);
          }
          previousLotIdRef.current = lotId;
          previousSuperadminRef.current = isSuperadmin;
          setIsSidebarReady(true);
        }
      }
    };

    loadLotData();

    return () => {
      isCancelled = true;
    };
  }, [lotId, user?.userId, isSuperadmin]);

  useEffect(() => {
    let isCancelled = false;

    const ensureUserLots = async () => {
      if (!user?.userId) {
        return;
      }

      if (isSuperadmin) {
        setShowChangeLot(false);
        localStorage.setItem("user_has_multiple_lots", "false");
        return;
      }

      if (userLots && userLots.length > 0) {
        const hasMultipleLots = userLots.length > 1;
        setShowChangeLot(hasMultipleLots);
        localStorage.setItem("user_has_multiple_lots", hasMultipleLots ? "true" : "false");
        return;
      }

      try {
        const lots = await fetchUserLots();
        if (!isCancelled) {
          const hasMultipleLots = lots.length > 1;
          setShowChangeLot(hasMultipleLots);
          localStorage.setItem("user_has_multiple_lots", hasMultipleLots ? "true" : "false");
        }
      } catch (error) {
        console.error("Failed to fetch user lots", error);
      }
    };

    ensureUserLots();

    return () => {
      isCancelled = true;
    };
  }, [userLots, fetchUserLots, user?.userId, isSuperadmin]);

  useEffect(() => {
    if (lotContext?.lotData && lotContext.lotData.lotId === lotId) {
      if (lotContext.lotData.lotName) {
        setLotName(lotContext.lotData.lotName);
        localStorage.setItem(`lot_${lotId}_name`, lotContext.lotData.lotName);
      }
      setIsBetaAiEnabled(!!lotContext.lotData.allowBetaAi);
    }
  }, [lotContext?.lotData, lotId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isMobile, isCollapsed]);

  useEffect(() => {
    if (!isMobile) return;
    setIsSidebarOpen(false);
  }, [isMobile, location.pathname, setIsSidebarOpen]);

  useEffect(() => {
    if (isCollapsed || isMobile) {
      setIsAccountMenuOpen(false);
      setIsLotMenuOpen(false);
    }
  }, [isCollapsed, isMobile]);

  if (!lotId) return null;

  // Display name with multi-lot indicator
  const displayLotName = isMultiLotMode ? getDisplayLotName() : lotName;
  const truncatedLotName =
    displayLotName.length > 36 ? displayLotName.substring(0, 36) + "..." : displayLotName;
  const mobileLotName =
    displayLotName.length > 20 ? displayLotName.substring(0, 20) + "..." : displayLotName;
  const userLotsCount = Array.isArray(userLots) ? userLots.length : 0;
  const canChangeLot = showChangeLot || userLotsCount > 1;

  // Helper to check if a page is restricted in multi-lot mode
  const isRestrictedPage = (path: string): boolean => {
    const parts = path.split("/");
    const isSuperadminPath = parts[3] === "superadmin";
    const segment = isSuperadminPath ? parts[4] : parts[3] || "";
    return NAV_SECTIONS.some((section) =>
      section.items.some((item) => item.path === segment && item.restrictedInMultiLot)
    );
  };

  // Handle restricted page navigation in multi-lot mode
  const handleRestrictedNavigation = (path: string): boolean => {
    if (isMultiLotMode && isRestrictedPage(path)) {
      setRestrictedPageTarget(path);
      setShowRestrictedPageModal(true);
      return true; // Navigation prevented
    }
    return false; // Allow normal navigation
  };

  // Confirm restricted page navigation (clears to single lot)
  const confirmRestrictedNavigation = () => {
    clearToSingleLot();
    setShowRestrictedPageModal(false);
    if (restrictedPageTarget) {
      navigate(restrictedPageTarget);
      setRestrictedPageTarget(null);
    }
  };

  const handleLogout = async () => {
    if (isAccountSuperadmin && isSuperadminModeEnabled()) {
      setSuperadminModeEnabled(false);
    }
    await logout();
  };

  const handleSuperadminToggle = useCallback(() => {
    if (!isAccountSuperadmin) {
      return;
    }
    setSuperadminModeEnabled(!isSuperadmin);
  }, [isAccountSuperadmin, isSuperadmin]);

  const toggleSidebar = () => {
    if (walkthroughActive) return;
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (walkthroughActive && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [walkthroughActive, isCollapsed]);

  const navigateToLotDashboard = () => {
    navigate("/lot-dashboard");
  };


  const normalizeRole = (value?: string | null): UserRole => {
    const normalized = (value || "").toLowerCase();
    if (normalized === "owner" || normalized === "operator" || normalized === "superadmin" || normalized === "staff") {
      return normalized as UserRole;
    }
    return "";
  };

  const handleLotSwitch = useCallback(async (targetLot: any) => {
    if (!targetLot?.lotId || targetLot.lotId === lotId) return;

    setIsLotMenuOpen(false);
    if (fadeTimeoutRef.current) {
      window.clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    setShouldFadeIn(false);
    setIsSidebarReady(false);

    const nextLotId = targetLot.lotId;
    const pathRemainder = location.pathname.replace(/^\/lot\/[^/]+/, "");
    const search = location.search || "";
    const hash = location.hash || "";

    const fallbackPath = `/lot/${nextLotId}/dashboard/${lastDashboardTab}`;
    const candidatePath = pathRemainder ? `/lot/${nextLotId}${pathRemainder}` : fallbackPath;

    let allowParallelAi: boolean | undefined = typeof targetLot?.allowBetaAi === "boolean" ? !!targetLot.allowBetaAi : undefined;
    let derivedRole: UserRole = normalizeRole(targetLot?.role as string);

    try {
      const lotResponse = await lotService.getLotById(nextLotId);
      const normalized = lotResponse && lotResponse.data ? lotResponse.data : lotResponse;
      if (normalized) {
        if (typeof normalized.allowBetaAi === "boolean") {
          allowParallelAi = !!normalized.allowBetaAi;
        }

        if (!isSuperadmin) {
          if (normalized.ownerId === user?.userId) {
            derivedRole = "owner";
          } else {
            try {
              const operatorsList = await lotService.getOperators(nextLotId);
              const isOperator = operatorsList?.some((op: any) => op.userId === user?.userId);
              derivedRole = isOperator ? "operator" : "staff";
            } catch (operatorError) {
              console.error("Failed to fetch operators for lot switch", operatorError);
              derivedRole = derivedRole || "staff";
            }
          }
        }
      }
    } catch (lotError) {
      console.error("Failed to fetch lot details for switch", lotError);
    }

    const effectiveRole: UserRole = isSuperadmin ? "superadmin" : (derivedRole || userRole || "staff");
    const lotAllowsParallelAi = allowParallelAi !== undefined ? !!allowParallelAi : isBetaAiEnabled;
    const allowedSections = new Set<string>();
    for (const section of NAV_SECTIONS) {
      for (const item of getVisibleItems(section, effectiveRole, isSuperadmin, lotAllowsParallelAi)) {
        allowedSections.add(item.path);
      }
    }

    let nextPath = candidatePath;
    try {
      const url = new URL(candidatePath, window.location.origin);
      const section = url.pathname.split("/")[3] || "dashboard";
      if (!allowedSections.has(section)) {
        nextPath = fallbackPath;
      }
    } catch (error) {
      nextPath = fallbackPath;
    }

    setUserRole(effectiveRole);
    setIsBetaAiEnabled(lotAllowsParallelAi);
    if (nextPath === candidatePath) {
      navigate(`${candidatePath}${search}${hash}`);
    } else {
      navigate(fallbackPath);
    }
  }, [lotId, location.pathname, location.search, location.hash, lastDashboardTab, user?.userId, userRole, isSuperadmin, navigate, setUserRole, setIsLotMenuOpen, setIsSidebarReady]);

  const lotMenuItems: MenuItem[] = useMemo(() => {
    if (!userLots || userLots.length === 0) return [];

    const isMultiSelectActive = selectedLots.length > 1;

    return userLots.map((lot, index) => {
      const lotKey = lot.lotId || lot.lotName || `lot-${index}`;
      const lotNameLabel = lot.lotName || "Unnamed Lot";
      const isPrimary = lot.lotId === primaryLotId;
      const isSecondarySelected = selectedLots.some(l => l.lotId === lot.lotId) && !isPrimary;
      const classNames = [
        isSecondarySelected && isMultiSelectActive ? "secondary-selected" : "",
        isPrimary && isMultiSelectActive ? "primary-selected" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return {
        key: lotKey,
        label: lotNameLabel,
        selected: isPrimary,
        className: classNames,
        closeOnSelect: !(isShiftHeld && !isMobile && !isMultiLotMobileDisabled),
        onSelect: () => {
          // If shift is held and not on mobile, toggle secondary selection
          if (isShiftHeld && !isMobile && !isMultiLotMobileDisabled) {
            toggleLotSelection(lot);
          } else {
            // Normal click - switch primary lot
            handleLotSwitch(lot);
          }
        },
      };
    });
  }, [userLots, lotId, primaryLotId, selectedLots, isShiftHeld, isMobile, isMultiLotMobileDisabled, handleLotSwitch, toggleLotSelection]);

  const toggleCategory = (category: CategoryKey) => {
    setOpenCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleNavigation = (to: string) => {
    if (walkthroughActive) {
      // Drop any in-progress walkthrough edits and proceed without confirmation.
      setIsDirty(false);
      navigate(to);
      return;
    }
    if (isDirty) {
      setPendingNavigation(to);
      setShowUnsavedChangesModal(true);
    } else {
      navigate(to);
    }
  };

  const openAccountModal = useCallback(() => {
    setIsAccountModalOpen(true);
    setIsAccountMenuOpen(false);
    setIsSidebarOpen(false);
  }, [setIsAccountMenuOpen, setIsAccountModalOpen, setIsSidebarOpen]);

  const closeAccountModal = useCallback(() => {
    setIsAccountModalOpen(false);
  }, []);

  const renderNavItem = (
    to: string,
    label: string,
    icon: string,
    options?: { disabled?: boolean; onClick?: () => void }
  ) => {
    const isDisabled = Boolean(options?.disabled);
    const isRestricted = isMultiLotMode && isRestrictedPage(to);
    let iconVariant = icon;

    if (label === "Notifications") {
      const hasUnreadNotifications = unreadNotificationCount && unreadNotificationCount > 0;
      const isActive = location.pathname === to;
      if (isActive) {
        iconVariant = `${icon}Selected`;
      } else if (hasUnreadNotifications) {
        iconVariant = `${icon}Selected`;
      }
    } else {
      iconVariant = location.pathname === to ? `${icon}Selected` : icon;
    }

    const itemClass = [
      "sidebar-item",
      location.pathname === to ? "active" : "",
      isDisabled ? "sidebar-item-disabled" : "",
      isRestricted ? "sidebar-item-multi-lot-restricted" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const action = () => {
      if (options?.onClick) {
        options.onClick();
        return;
      }
      // Check if this is a restricted page in multi-lot mode
      if (handleRestrictedNavigation(to)) return;
      handleNavigation(to);
    };

    return (
      <div
        key={label}
        className={`sidebar-item-wrapper${isDisabled ? " sidebar-item-wrapper-disabled" : ""}${isRestricted ? " sidebar-item-wrapper-restricted" : ""}`}
        data-nav-path={to}
      >
        <div
          className={itemClass}
          onClick={(e) => {
            if (isDisabled) return;
            e.preventDefault();
            e.stopPropagation();
            action();
          }}
          onKeyDown={(event) => {
            if (isDisabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              action();
            }
          }}
          role={isDisabled ? undefined : "button"}
          tabIndex={isDisabled ? -1 : 0}
          aria-disabled={isDisabled}
          onMouseEnter={(e) => {
            const isActive = location.pathname === to;
            if (isActive) return;
            if (icon === "Notifications" && unreadNotificationCount && unreadNotificationCount > 0) return;
            const img = (e.currentTarget as HTMLElement).querySelector("img") as HTMLImageElement | null;
            if (!img) return;
            if (animTimeoutRef.current) window.clearTimeout(animTimeoutRef.current);
            animImgRef.current = img;
            img.onerror = () => {
              img.onerror = null;
              img.src = `/assets/nav/${iconVariant}.svg`;
              animImgRef.current = null;
              if (animTimeoutRef.current) {
                window.clearTimeout(animTimeoutRef.current);
                animTimeoutRef.current = null;
              }
            };
            img.src = `/assets/nav/animated/${icon}Animated.svg?t=${Date.now()}`;
            animTimeoutRef.current = window.setTimeout(() => {
              if (animImgRef.current && animImgRef.current.src.includes("Animated")) {
                animImgRef.current.src = `/assets/nav/${iconVariant}.svg`;
              }
              animImgRef.current = null;
              animTimeoutRef.current = null;
            }, 850);
          }}
        >
          <img src={`/assets/nav/${iconVariant}.svg`} alt={label} />
          {!isCollapsed && <span className={location.pathname === to ? "active-text" : ""}>{label}</span>}
        </div>
      </div>
    );
  };

  const determineUserRole = (): UserRole => {
    if (isSuperadmin) {
      return "superadmin";
    }

    if (!user || !lotId) {
      return "";
    }

    if (lotContext?.lotData?.ownerId === user.userId) {
      return "owner";
    }

    if (operators.some((op: any) => op.userId === user.userId)) {
      return "operator";
    }

    if (lotContext?.lotData || operators.length > 0) {
      return "staff";
    }

    return "";
  };

  const effectiveUserRole: UserRole = userRole || determineUserRole();

  const renderThemeIcon = (size = 15) => {
    if (themeMode === "dark") {
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    if (themeMode === "light") {
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
          <path
            d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    }

    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 3a6 6 0 0 0 6 6c0 3.314-2.686 6-6 6s-6-2.686-6-6c0-3.314 2.686-6 6-6z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  const renderLoadingSidebar = () => {
    const containerClasses = [
      "sidebar",
      isMobile ? "mobile" : "desktop",
      !isMobile && isCollapsed ? "collapsed" : "",
      isMobile && isSidebarOpen ? "open" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const loadingStateClasses = [
      "sidebar-loading-state",
      !isMobile && isCollapsed ? "collapsed" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const skeletonItems = Array.from({ length: 8 }, (_, index) => (
      <div
        key={`sidebar-skeleton-item-${index}`}
        className="skeleton-block sidebar-skeleton-item"
        aria-hidden="true"
      />
    ));

    return (
      <div className={containerClasses}>
        <div className={loadingStateClasses}>
          <div className="sidebar-loading-inner">
            <div className="sidebar-loading-header">
              <div className="sidebar-brand-row">
                {!isCollapsed && (
                  <img
                    src="/assets/logos/Logo_Parallel_Thin.svg"
                    alt="Parallel"
                    className="sidebar-logo"
                  />
                )}
                {!isMobile ? (
                  <button
                    className="sidebar-toggle"
                    onClick={toggleSidebar}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  >
                    <img
                      src={
                        isCollapsed
                          ? "/assets/nav/SidebarClosed.svg"
                          : "/assets/nav/SidebarOpened.svg"
                      }
                      alt=""
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <button
                    className="sidebar-close"
                    onClick={() => setIsSidebarOpen(false)}
                    aria-label="Close sidebar"
                  >
                    <img src="/assets/nav/close.svg" alt="Close sidebar" />
                  </button>
                )}
              </div>
            </div>

            <div className="sidebar-loading-body">
              <div
                className="skeleton-block sidebar-skeleton-lot"
                aria-hidden="true"
              />
              <div className="sidebar-skeleton-items skeleton-stack">{skeletonItems}</div>
            </div>

            <div className="sidebar-loading-footer">
              <div
                className="skeleton-block sidebar-skeleton-account"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboardItem = () => {
    const isActive = location.pathname.startsWith(`/lot/${lotId}/dashboard`);
    const iconName = isActive ? "DashboardSelected" : "Dashboard";

    return (
      <div key="dashboard" className="sidebar-item-wrapper">
        <div
          className={`sidebar-item${isActive ? " active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleNavigation(`/lot/${lotId}/dashboard/${lastDashboardTab}`);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleNavigation(`/lot/${lotId}/dashboard/${lastDashboardTab}`);
            }
          }}
          role="button"
          tabIndex={0}
          onMouseEnter={(e) => {
            if (isActive) return;
            const img = (e.currentTarget as HTMLElement).querySelector("img") as HTMLImageElement | null;
            if (!img) return;
            if (animTimeoutRef.current) window.clearTimeout(animTimeoutRef.current);
            animImgRef.current = img;
            img.onerror = () => {
              img.onerror = null;
              img.src = `/assets/nav/${iconName}.svg`;
              animImgRef.current = null;
              if (animTimeoutRef.current) {
                window.clearTimeout(animTimeoutRef.current);
                animTimeoutRef.current = null;
              }
            };
            img.src = `/assets/nav/animated/DashboardAnimated.svg?t=${Date.now()}`;
            animTimeoutRef.current = window.setTimeout(() => {
              if (animImgRef.current && animImgRef.current.src.includes("Animated")) {
                animImgRef.current.src = `/assets/nav/${iconName}.svg`;
              }
              animImgRef.current = null;
              animTimeoutRef.current = null;
            }, 850);
          }}
        >
          <img src={`/assets/nav/${iconName}.svg`} alt="Dashboard" />
          {!isCollapsed && (
            <span className={isActive ? "active-text" : ""}>Dashboard</span>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (
    key: CategoryKey,
    label: string,
    items: React.ReactNode[],
    options?: { collapsible?: boolean }
  ) => {
    if (!items.length) return null;

    const isCollapsible = options?.collapsible !== false;
    const sectionIsOpen = isCollapsed || !isCollapsible || openCategories[key];

    return (
      <div className="sidebar-section" key={key}>
        {!isCollapsed && (
          isCollapsible ? (
            <button
              type="button"
              className="sidebar-section-header"
              onClick={() => toggleCategory(key)}
              aria-expanded={sectionIsOpen}
            >
              <span>{label}</span>
              <svg
                className={`section-chevron${sectionIsOpen ? " open" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M3 5l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <div className="sidebar-section-header sidebar-section-header-static">
              <span>{label}</span>
            </div>
          )
        )}
        <div
          className={`sidebar-section-items${sectionIsOpen ? " open" : " closed"}${
            isCollapsed ? " collapsed" : ""
          }`}
        >
          {items.map((item, index) => (
            <React.Fragment key={index}>{item}</React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderDesktopFooter = () => {
    const collapsedThemeLabel =
      themeMode === "default" ? "System" : themeMode === "dark" ? "Dark" : "Light";

    if (isCollapsed) {
      return (
        <div className="sidebar-footer desktop-footer collapsed">
          {!isSuperadmin && (
            <button
              type="button"
              className="sidebar-footer-icon"
              onClick={openAccountModal}
              aria-label="Account"
            >
              <img src="/assets/nav/Account.svg" alt="" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="sidebar-footer-icon"
            onClick={cycleThemeMode}
            aria-label={`Cycle theme (current: ${collapsedThemeLabel})`}
          >
            {renderThemeIcon(15)}
          </button>
          <button
            type="button"
            className="sidebar-footer-icon"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <img src="/assets/nav/Logout.svg" alt="" aria-hidden="true" />
          </button>
        </div>
      );
    }

    const accountMenuItems: MenuItem[] = [];
    const themeLabel =
      themeMode === "dark" ? "Dark Theme" : themeMode === "light" ? "Light Theme" : "System Theme";

    accountMenuItems.push({
      key: "account",
      label: "Account",
      icon: <img src="/assets/nav/Account.svg" alt="" aria-hidden="true" />,
      onSelect: openAccountModal,
    });

    accountMenuItems.push({
      key: "theme",
      label: themeLabel,
      icon: renderThemeIcon(14),
      onSelect: cycleThemeMode,
      closeOnSelect: false,
    });

    accountMenuItems.push({
      key: "logout",
      label: "Logout",
      icon: <img src="/assets/nav/Logout.svg" alt="" aria-hidden="true" />,
      onSelect: handleLogout,
      tone: "danger",
    });

    if (isAccountSuperadmin) {
      accountMenuItems.push({
        key: "superadmin-mode",
        label: "Superadmin",
        icon: (
          <img
            src={
              isSuperadmin
                ? "/assets/superadmin/superadmin_disable.svg"
                : "/assets/superadmin/superadmin_enable.svg"
            }
            alt=""
            aria-hidden="true"
          />
        ),
        onSelect: handleSuperadminToggle,
        className: "superadmin-menu-item",
      });
    }

    return (
      <div className="sidebar-footer desktop-footer">
        <Menu
          className="account-menu-container"
          trigger={
            <button
              type="button"
              className={[
                "user-menu-trigger",
                isSuperadmin ? "superadmin-enabled" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-haspopup="true"
              aria-expanded={isAccountMenuOpen}
            >
              <div className="user-menu-text">
                <span className="user-menu-name">{user?.name || user?.email || "Account"}</span>
                {user?.email && <span className="user-menu-email">{user.email}</span>}
              </div>
              <img
                src="/assets/nav/arrow_up_down.svg"
                alt=""
                aria-hidden="true"
                className={`user-menu-arrow${isAccountMenuOpen ? " open" : ""}`}
              />
            </button>
          }
          items={accountMenuItems}
          placement="top-start"
          dropdownClassName="user-menu-dropdown"
          onOpenChange={setIsAccountMenuOpen}
        />
      </div>
    );
  };

  const sidebarClassDesktop = [
    "sidebar",
    "desktop",
    isCollapsed ? "collapsed" : "",
    shouldFadeIn ? "sidebar-fade-in" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Filter NAV_SECTIONS to the items the current user can see
  const visibleSections = NAV_SECTIONS.map((section) => ({
    section,
    items: getVisibleItems(section, effectiveUserRole, isSuperadmin, isBetaAiEnabled),
  })).filter(({ items }) => items.length > 0);

  const renderDesktopSidebar = () => (
    <div className={sidebarClassDesktop}>
      <div className="sidebar-header desktop-header">
        <div className="sidebar-brand-row">
          {!isCollapsed && (
            <img
              src="/assets/logos/Logo_Parallel_Thin.svg"
              alt="Parallel"
              className="sidebar-logo"
            />
          )}
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            disabled={walkthroughActive}
            aria-disabled={walkthroughActive}
          >
            <img
              src={
                isCollapsed
                  ? "/assets/nav/SidebarClosed.svg"
                  : "/assets/nav/SidebarOpened.svg"
              }
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
        {canChangeLot ? (
          <Menu
            trigger={
              <button
                type="button"
                className="sidebar-lot-button"
                onClick={() => {
                  if (isCollapsed) {
                    navigateToLotDashboard();
                  } else {
                    setIsLotMenuOpen((prev) => !prev);
                  }
                }}
              >
                {!isCollapsed && <span className="sidebar-lot-name">{truncatedLotName}</span>}
                {!isCollapsed ? (
                  <img
                    src="/assets/nav/arrow_up_down.svg"
                    alt="Change lot"
                    className={`sidebar-lot-icon${isLotMenuOpen ? " open" : ""}`}
                  />
                ) : (
                  <img src="/assets/BackIcon.svg" alt="Change lot" className="sidebar-lot-icon" />
                )}
              </button>
            }
            items={lotMenuItems}
            placement={isCollapsed ? "right-start" : "bottom-start"}
            header={{
              backLabel: "Back",
              onBack: navigateToLotDashboard,
              onTitleClick: navigateToLotDashboard,
              backIcon: <img src="/assets/BackIcon.svg" alt="" aria-hidden="true" />,
            }}
            onOpenChange={setIsLotMenuOpen}
            allowNoSelection={false}
          />
        ) : (
          <button
            type="button"
            className="sidebar-lot-button"
            aria-disabled="true"
            tabIndex={-1}
            disabled
          >
            {!isCollapsed && <span className="sidebar-lot-name">{truncatedLotName}</span>}
          </button>
        )}
      </div>
      <nav className="sidebar-menu desktop-menu">
        {visibleSections.map(({ section, items }) =>
          renderSection(
            section.key,
            section.label,
            items.map((item) => {
              if (item.path === "dashboard") return renderDashboardItem();
              const fullPath = item.superadminPath
                ? `/lot/${lotId}/superadmin/${item.path}`
                : `/lot/${lotId}/${item.path}`;
              return renderNavItem(fullPath, item.label, item.icon, {
                disabled: item.disabledOnMobile ? isMobile : false,
              });
            }),
            { collapsible: section.collapsible }
          )
        )}
      </nav>
      {renderDesktopFooter()}
    </div>
  );

  const sidebarClassMobile = [
    "sidebar",
    "mobile",
    isSidebarOpen ? "open" : "",
    shouldFadeIn ? "sidebar-fade-in" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderBackdrop = () =>
    isMobile && isSidebarOpen ? (
      <div className="sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} />
    ) : null;

  const renderMobileSidebar = () => (
    <>
      {renderBackdrop()}
      <div className={sidebarClassMobile}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/assets/logos/Logo_Parallel_Thin.svg" alt="Parallel" className="logo" />
          </div>
          <button
            className="sidebar-close"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <img src="/assets/nav/close.svg" alt="Close sidebar" />
          </button>
        </div>
        <nav className="sidebar-menu">
          {canChangeLot && (
            <div className="sidebar-item-wrapper">
              <NavLink to="/lot-dashboard" className="sidebar-item">
                <img className="back-icon-mobile" src="/assets/BackIcon.svg" alt="Back" />
                <span>Change ({mobileLotName})</span>
              </NavLink>
            </div>
          )}
          {visibleSections.flatMap(({ section, items }) => {
            const elements: React.ReactNode[] = [];
            if (section.superadminOnly) {
              elements.push(
                <div key={`${section.key}-header`} className="sidebar-section-header sidebar-section-header-static">
                  <span>{section.label}</span>
                </div>
              );
            }
            items.forEach((item) => {
              if (item.path === "dashboard") {
                elements.push(renderDashboardItem());
                return;
              }
              const fullPath = item.superadminPath
                ? `/lot/${lotId}/superadmin/${item.path}`
                : `/lot/${lotId}/${item.path}`;
              elements.push(
                renderNavItem(fullPath, item.label, item.icon, {
                  disabled: item.disabledOnMobile ? isMobile : false,
                })
              );
              // Account modal link appears after Settings on mobile (non-superadmin only)
              if (item.path === "settings" && !isSuperadmin) {
                elements.push(
                  renderNavItem("#account", "Account", "Account", { onClick: openAccountModal })
                );
              }
            });
            return elements;
          })}

          {isAccountSuperadmin && (
            <div className="sidebar-item-wrapper">
              <button
                type="button"
                className={`sidebar-item superadmin-toggle ${isSuperadmin ? "active" : ""}`.trim()}
                onClick={handleSuperadminToggle}
              >
                <img
                  src={
                    isSuperadmin
                      ? "/assets/superadmin/superadmin_disable.svg"
                      : "/assets/superadmin/superadmin_enable.svg"
                  }
                  alt="Toggle superadmin mode"
                />
                <span>{isSuperadmin ? "Disable Superadmin" : "Enable Superadmin"}</span>
              </button>
            </div>
          )}

          <div className="sidebar-item-wrapper">
            <div className="sidebar-item" onClick={cycleThemeMode}>
              {renderThemeIcon(15)}
              <span>
                {themeMode === "default"
                  ? "System"
                  : themeMode === "dark"
                  ? "Dark"
                  : "Light"}
              </span>
            </div>
          </div>
          <div className="sidebar-item-wrapper" onClick={handleLogout}>
            <div className="sidebar-item logout">
              <img src="/assets/nav/Logout.svg" alt="Logout" />
              <span>Logout</span>
            </div>
          </div>
        </nav>
      </div>
    </>
  );

  if (!isSidebarReady) {
    return renderLoadingSidebar();
  }

  return (
    <>
      {isMobile ? renderMobileSidebar() : renderDesktopSidebar()}
      {showUnsavedChangesModal && (
        <Modal
          isOpen={true}
          title="You have unsaved changes!"
          description="The changes you made will not be applied unless you save before switching to another page."
          confirmText="Take me back!"
          cancelText="Continue Anyway"
          onConfirm={() => {
            setShowUnsavedChangesModal(false);
            setPendingNavigation(null);
          }}
          onCancel={() => {
            if (pendingNavigation) {
              setIsDirty(false);
              navigate(pendingNavigation);
              setPendingNavigation(null);
            }
            setShowUnsavedChangesModal(false);
          }}
          swapKeyboardActions={true}
        />
      )}
      {showRestrictedPageModal && (
        <Modal
          isOpen={true}
          title="Multiple Lots Selected"
          description={`You have multiple parking lots selected. Proceeding will unselect all but "${getPrimaryLot()?.lotName || 'the primary lot'}".`}
          confirmText="Continue"
          cancelText="Cancel"
          onConfirm={confirmRestrictedNavigation}
          onCancel={() => {
            setShowRestrictedPageModal(false);
            setRestrictedPageTarget(null);
          }}
        />
      )}
      <AccountModal isOpen={isAccountModalOpen} onClose={closeAccountModal} />
    </>
  );
};

export default Sidebar;
